import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";

// Dynamically load the correct worker version
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function InvoicePage() {
  const { jobId } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    if (!authToken) {
      setError("Authentication required. Please log in.");
      setLoading(false);
      return;
    }

    console.log("Generating invoice for jobId:", jobId);

    const generateInvoice = async () => {
      try {
        const response = await fetch(`https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/invoice`, {
          method: "POST",
          headers: { 
            Authorization: `Bearer ${authToken}`,
            "Accept": "application/pdf"
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error:", response.status, errorText);
          setError(`❌ Failed to generate invoice. Server returned: ${response.status} ${response.statusText}`);
          return;
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/pdf")) {
          console.error("Unexpected content type:", contentType);
          setError(`❌ Server did not return a PDF (got ${contentType || "unknown type"})`);
          return;
        }

        setSuccessMessage("🎉 Invoice successfully generated! 🎉");
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error("Error generating invoice:", err);
        setError(`❌ Error generating invoice: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    generateInvoice();
  }, [jobId, authToken]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleDocumentLoadSuccess = ({ numPages }) => {
    console.log("PDF loaded successfully with", numPages, "pages");
    setNumPages(numPages);
  };
  
  const handleDocumentLoadError = (error) => {
    console.error("Error loading PDF document:", error);
    setError(`❌ Error loading PDF: ${error.message}`);
  };

  const handlePageChange = (delta) => {
    setCurrentPage((prevPage) => Math.max(1, Math.min(prevPage + delta, numPages)));
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `invoice-${jobId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setPdfUrl(null);
    setNumPages(null);
    setCurrentPage(1);
    setSuccessMessage(null);
    
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-blue-500 font-bold text-xl">
        🎨 Generating your invoice... ✨
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col justify-center items-center h-screen text-red-500 font-bold">
        <div className="text-xl mb-4">{error}</div>
        <button 
          onClick={handleRetry} 
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
        >
          Try Again
        </button>
      </div>
    );

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#f8f9fa]">
      <div className="w-full max-w-2xl bg-white p-6 rounded-lg shadow-lg text-center">
        {successMessage && <div className="mb-4 text-green-500 font-bold text-lg">{successMessage}</div>}

        <h1 className="text-2xl font-extrabold text-gray-800 mb-4">🧾 Invoice for Job ID: {jobId}</h1>

        <div className="mb-4 flex justify-center">
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700"
          >
            💾 Download PDF
          </button>
        </div>

        <div className="flex flex-col items-center bg-gray-100 p-4 rounded-lg shadow-md">
          {pdfUrl ? (
            <Document 
              file={pdfUrl} 
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={<div className="text-blue-500">Loading PDF...</div>}
              error={<div className="text-red-500">Failed to load PDF. Try downloading instead.</div>}
            >
              <Page
                pageNumber={currentPage}
                className="mb-4 border border-gray-300 rounded-lg shadow-sm"
                width={500}
              />
            </Document>
          ) : (
            <div className="text-yellow-500 font-bold">PDF URL not available</div>
          )}

        </div>
      </div>
    </div>
  );
}
